import { Button, Checkbox, IconButton, TextField } from '@ads/components-react';

import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '@/shared/ui/atoms';
import type { ExamPublication } from '../../../types/exam';

export interface EditPublicationModalProps {
  readonly publication: ExamPublication | null;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (data: {
    publicationId: number;
    startDate: string;
    endDate: string;
    maxAttempts: number;
    durationMinutes: number;
    allowExtraAttempt: boolean;
  }) => void;
}

export function EditPublicationModal({
  publication,
  open,
  onClose,
  onSave,
}: EditPublicationModalProps) {
  const { t } = useTranslation('examManagement');

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [durationMinutes, setDurationMinutes] = useState(120);
  const [allowExtraAttempt, setAllowExtraAttempt] = useState(false);

  useEffect(() => {
    if (publication) {
      setStartDate(publication.startDate);
      setEndDate(publication.endDate);
      setMaxAttempts(publication.maxAttempts);
      setDurationMinutes(publication.durationMinutes);
      setAllowExtraAttempt(publication.allowExtraAttempt);
    }
  }, [publication]);

  const handleSave = () => {
    if (!publication) return;
    onSave({
      publicationId: publication.id,
      startDate,
      endDate,
      maxAttempts,
      durationMinutes,
      allowExtraAttempt,
    });
  };

  if (!publication) return null;

  return (
    <Dialog.Root open={open} onOpenChange={o => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-md py-xs">
            <Dialog.Title className="text-ctx-content-title ts-subtitle-sm ">
              {t('publications.editModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('actions.cancel')}
              />
            </Dialog.Close>
          </div>

          <div className="flex flex-col gap-mini overflow-y-auto px-md py-md">
            <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xxs">
              <span className="ts-caption-xxs text-ctx-content-placeholder">
                {t('publications.editModal.examTitle')}
              </span>
              <p className="ts-paragraph-xs !font-medium text-ctx-content-title">
                {publication.lessonPlanTitle}
              </p>
              <span className="ts-caption-xxs text-ctx-content-title">
                {publication.classGroup} — {publication.professorName}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-md">
              <DatePicker
                label={t('publications.editModal.startDate')}
                value={startDate}
                onChange={setStartDate}
              />
              <DatePicker
                label={t('publications.editModal.endDate')}
                value={endDate}
                onChange={setEndDate}
              />
            </div>

            <div className="grid grid-cols-2 gap-xs">
              <TextField
                custom={{
                  input: '!w-full',
                }}
                label={t('publications.editModal.maxAttempts')}
                placeholder={t('publications.editModal.maxAttempts')}
                type="number"
                size="md"
                value={String(maxAttempts)}
                onChange={e => setMaxAttempts(Number(e.target.value))}
              />
              <TextField
                label={t('publications.editModal.duration')}
                type="number"
                custom={{
                  input: '!w-full',
                }}
                placeholder={t('publications.editModal.maxAttempts')}
                size="md"
                value={String(durationMinutes)}
                onChange={e => setDurationMinutes(Number(e.target.value))}
              />
            </div>

            <Checkbox
              label={t('publications.editModal.allowExtra')}
              checked={allowExtraAttempt}
              onCheckedChange={checked => {
                if (checked !== 'indeterminate') {
                  setAllowExtraAttempt(checked);
                }
              }}
            />
          </div>

          <div className="flex items-center justify-end gap-mini border-t-hairline border-ctx-layout-border px-md py-xs">
            <Button hierarchy="secondary" size="md" onClick={onClose}>
              {t('actions.cancel')}
            </Button>
            <Button size="md" onClick={handleSave}>
              {t('actions.save')}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
